import { useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

export default function SwipeBack({ children }) {
  const navigate = useNavigate();
  const touchStart = useRef(null);
  const touchStartY = useRef(null);
  const [swipeProgress, setSwipeProgress] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const locked = useRef(false);

  const onTouchStart = useCallback((e) => {
    const x = e.touches[0].clientX;
    // Only trigger from left 40px edge
    if (x > 40) return;
    touchStart.current = x;
    touchStartY.current = e.touches[0].clientY;
    locked.current = false;
  }, []);

  const onTouchMove = useCallback((e) => {
    if (touchStart.current === null) return;
    const dx = e.touches[0].clientX - touchStart.current;
    const dy = Math.abs(e.touches[0].clientY - touchStartY.current);

    // If vertical scroll dominates, cancel swipe
    if (!locked.current && dy > 15) { touchStart.current = null; setSwipeProgress(0); setSwiping(false); return; }
    if (dx > 10) locked.current = true;

    if (dx > 0 && locked.current) {
      const progress = Math.min(dx / 150, 1);
      setSwipeProgress(progress);
      setSwiping(true);
    }
  }, []);

  const onTouchEnd = useCallback(() => {
    if (swipeProgress > 0.45) {
      // If a game is active, dispatch event to trigger the game's exit warning instead
      if (window.__gfgGameActive) {
        window.dispatchEvent(new Event('gfg-swipe-back'));
      } else {
        navigate(-1);
      }
    }
    touchStart.current = null;
    setSwipeProgress(0);
    setSwiping(false);
    locked.current = false;
  }, [swipeProgress, navigate]);

  return (
    <div
      className="md:hidden"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{ position: 'fixed', top: 0, left: 0, width: '40px', height: '100%', zIndex: 98, touchAction: 'pan-y' }}
    >
      {/* Swipe indicator */}
      <AnimatePresence>
        {swiping && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
            className="fixed top-0 left-0 h-full pointer-events-none"
            style={{ zIndex: 98 }}
          >
            {/* Edge gradient */}
            <div className="absolute top-0 left-0 h-full transition-all"
              style={{
                width: `${swipeProgress * 80}px`,
                background: `linear-gradient(to right, rgba(185,21,204,${swipeProgress * 0.15}), transparent)`,
              }}
            />
            {/* Arrow indicator */}
            <motion.div
              className="absolute top-1/2 -translate-y-1/2 flex items-center justify-center w-9 h-9 rounded-full"
              style={{
                left: `${Math.max(swipeProgress * 70 - 10, 0)}px`,
                background: `rgba(185,21,204,${swipeProgress * 0.4})`,
                backdropFilter: 'blur(8px)',
                border: `1px solid rgba(185,21,204,${swipeProgress * 0.5})`,
                boxShadow: `0 0 ${swipeProgress * 20}px rgba(185,21,204,${swipeProgress * 0.3})`,
              }}
              animate={{ scale: swipeProgress > 0.45 ? 1.15 : 0.9 }}
            >
              <ChevronLeft className="w-4 h-4" style={{ color: `rgba(255,255,255,${swipeProgress})` }} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
